import { useEffect, useState } from "react";
import {
  Waves, Snowflake, Bubbles, Sprout, DoorClosed, Moon, Sparkles,
  UtensilsCrossed, TentTree, Lock, SprayCan, Droplets, Droplet, Shirt,
  Dumbbell, Music, Dices, Fan, Flame, Coffee, Wine, CookingPot, Gamepad2,
  Fence, Wifi, Tv, Briefcase, Monitor, Armchair, BedDouble, BookOpen,
  Umbrella, Sun, ShieldCheck, SquareParking, CalendarDays, MountainSnow,
  Building2, Soup, Cookie, Waves as WaterIcon,
} from "lucide-react";
import { useLang } from "@/i18n";

const FA_TO_LUCIDE = {
  "fa-water-ladder": Waves,
  "fa-snowflake": Snowflake,
  "fa-hot-tub-person": Bubbles,
  "fa-seedling": Sprout,
  "fa-door-closed": DoorClosed,
  "fa-moon": Moon,
  "fa-broom": Sparkles,
  "fa-utensils": UtensilsCrossed,
  "fa-bench-tree": TentTree,
  "fa-vault": Lock,
  "fa-spray-can-sparkles": SprayCan,
  "fa-pump-soap": Droplets,
  "fa-bottle-droplet": Droplet,
  "fa-shirt": Shirt,
  "fa-dumbbell": Dumbbell,
  "fa-piano-keyboard": Music,
  "fa-dice": Dices,
  "fa-fan": Fan,
  "fa-grill-hot": Flame,
  "fa-mug-hot": Coffee,
  "fa-wine-glass": Wine,
  "fa-kitchen-set": CookingPot,
  "fa-futbol": Gamepad2,
  "fa-fence": Fence,
  "fa-wifi": Wifi,
  "fa-tv": Tv,
  "fa-briefcase": Briefcase,
  "fa-desktop": Monitor,
  "fa-chair": Armchair,
  "fa-bed": BedDouble,
  "fa-book-open": BookOpen,
  "fa-umbrella-beach": Umbrella,
  "fa-water": WaterIcon,
  "fa-sun": Sun,
  "fa-shield-halved": ShieldCheck,
  "fa-square-parking": SquareParking,
  "fa-calendar-days": CalendarDays,
  "fa-mountain-sun": MountainSnow,
  "fa-city": Building2,
  "fa-bowl-rice": Soup,
  "fa-cookie": Cookie,
};

const NAME_FR = {
  "Air conditioning": "Climatisation",
  "Baby crib": "Lit bébé parapluie",
  "Baking sheet": "Plaque de four",
  "Balcony": "Balcon",
  "Barbeque utensils": "Barbecue",
  "Bay": "Vue sur la baie",
  "Beach": "Plage à proximité",
  "Beach view": "Vue plage",
  "Blender": "Blender",
  "Board games": "Jeux de société",
  "Body soap": "Savon",
  "Books": "Livres",
  "Books for kids": "Livres enfants",
  "Car recommended": "Voiture recommandée",
  "Ceiling fan": "Ventilateur de plafond",
  "City view": "Vue ville",
  "Cleaning before checkout": "Ménage avant départ",
  "Cleaning products": "Produits de ménage",
  "Clothing storage": "Penderie",
  "Coffee": "Café",
  "Coffee/tea maker": "Machine à café et thé",
  "Computer monitor": "Écran d'ordinateur",
  "Conditioner": "Après-shampoing",
  "Cooking basics": "Bases de cuisine",
  "Deck patio uncovered": "Terrasse ouverte",
  "Desk chair": "Chaise de bureau",
  "Dining table": "Table à manger",
  "Dishwasher": "Lave-vaisselle",
  "Drying rack for clothing": "Étendoir à linge",
  "Electric kettle": "Bouilloire électrique",
  "Essentials": "Essentiels de toilette",
  "Exercise equipment": "Espace fitness",
  "Fenced yard": "Jardin clôturé",
  "Fire Extinguisher": "Extincteur",
  "Foosball": "Baby-foot",
  "Free WiFi": "Wifi gratuit",
  "Free parking": "Parking gratuit",
  "Freezer": "Congélateur",
  "Garden or backyard": "Jardin privatif",
  "Hair Dryer": "Sèche-cheveux",
  "Hammock": "Hamac",
  "Hangers": "Cintres",
  "High chair": "Chaise haute",
  "Hot water": "Eau chaude",
  "Housekeeper on request": "Ménage sur demande",
  "Ice maker": "Machine à glaçons",
  "Internet": "Internet",
  "Iron": "Fer à repasser",
  "Iron board": "Table à repasser",
  "Jacuzzi": "Jacuzzi",
  "Kitchen": "Cuisine équipée",
  "Kitchen island": "Îlot de cuisine",
  "Kitchen utensils": "Ustensiles de cuisine",
  "Laptop Friendly workspace": "Espace travail ordinateur",
  "Laundromat": "Laverie",
  "Linens": "Linge de lit",
  "Long term stays allowed": "Séjours longue durée",
  "Marina": "Marina",
  "Microwave": "Micro-ondes",
  "Mini fridge": "Réfrigérateur compact",
  "Mountain view": "Vue montagne",
  "Near ocean": "Bord de mer",
  "Ocean view": "Vue mer",
  "Office": "Bureau",
  "Outdoor dining": "Repas en plein air",
  "Outdoor furniture": "Mobilier extérieur",
  "Outdoor grill": "Barbecue d'extérieur",
  "Outdoor sunloungers": "Transats",
  "Oven": "Four",
  "Piano": "Piano",
  "Portable fans": "Ventilateurs d'appoint",
  "Private entrance": "Entrée privative",
  "Private living room": "Salon privé",
  "Private parking": "Parking privé",
  "Private pool": "Piscine privée",
  "Refrigerator": "Réfrigérateur",
  "Rice maker": "Cuiseur à riz",
  "Room darkening shades": "Voilages occultants",
  "Safe": "Coffre-fort",
  "Security system": "Alarme & sécurité",
  "Shampoo": "Shampoing",
  "Shower gel": "Gel douche",
  "Single level home": "Maison de plain-pied",
  "Smart TV": "TV connectée",
  "Smoke detector": "Détecteur de fumée",
  "Sound system": "Système audio",
  "Stove": "Cuisinière",
  "Street parking": "Stationnement dans la rue",
  "Suitable for children": "Adapté aux enfants",
  "Suitable for infants": "Adapté aux bébés",
  "Swimming pool": "Piscine",
  "TV": "TV",
  "Toaster": "Grille-pain",
  "Toddler bed": "Lit enfant",
  "Towels": "Serviettes",
  "Tub": "Baignoire",
  "Video games": "Jeux vidéo",
  "Washing Machine": "Lave-linge",
  "Water view": "Vue sur l'eau",
  "WiFi speed (100+ Mbps)": "Wifi fibre 100+ Mbps",
  "Wine glasses": "Verres à vin",
  "Wireless": "Connexion sans fil",
};

const VISIBLE = 12;

export default function AmenityList({ listingId }) {
  const { lang, t } = useLang();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setExpanded(false);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/amenities/${listingId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (alive) setItems(d.amenities || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [listingId]);

  if (!Array.isArray(items) || items.length === 0) return null;

  const shown = expanded ? items : items.slice(0, VISIBLE);

  return (
    <div className="mt-8" data-testid="amenities">
      <p className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-gold mb-4">
        {t.booking.amenitiesTitle}
      </p>
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-3.5">
        {shown.map((a, i) => {
          const name = (a.name || "").trim();
          const Icon = FA_TO_LUCIDE[a.icon] || Sparkles;
          const frName = NAME_FR[name];
          const label =
            lang === "fr"
              ? (lang === "fr" && a.description) || frName || name
              : a.name;
          return (
            <div key={`${name}-${i}`} className="flex items-start gap-2.5">
              <Icon size={15} strokeWidth={1.75} className="text-gold shrink-0 mt-0.5" />
              <span className="text-xs text-mist font-light leading-snug">
                {label}
              </span>
            </div>
          );
        })}
      </div>
      {items.length > VISIBLE && (
        <button
          type="button"
          data-testid="amenities-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="mt-5 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-gold hover:text-goldlight transition-colors"
        >
          {expanded
            ? t.booking.amenitiesLess
            : `${t.booking.amenitiesMore} (${items.length - VISIBLE})`}
        </button>
      )}
    </div>
  );
}
